import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import {
  FileBarChart, Download, Printer, ChevronDown, ChevronRight,
  BarChart3, TrendingUp, Loader2, FileText,
} from 'lucide-react';
import clsx from 'clsx';
import { reportsApi, entitiesApi } from '@/api/client';
import SearchableSelect from '@/components/SearchableSelect';
import type { Entity } from '@/types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Column { key: string; label: string }

interface AccountRow {
  account_code: string;
  account_name: string;
  fs_line_item: string | null;
  parent_code: string | null;
  parent_name: string | null;
  amounts: Record<string, string>;
  indent_level?: number;
  is_subtotal?: boolean;
  node_id?: string;
  parent_node_id?: string | null;
}

interface SectionData {
  sections: {
    assets?: AccountRow[];
    liabilities?: AccountRow[];
    equity?: AccountRow[];
    revenue?: AccountRow[];
    expenses?: AccountRow[];
    tax?: AccountRow[];
    discontinued?: AccountRow[];
  };
  totals: Record<string, Record<string, string>>;
}

interface ReportData {
  entity_id: number;
  entity_name: string;
  entity_code: string;
  currency: string;
  year: number;
  scenario: string;
  view: string;
  balance_type: string;
  columns: Column[];
  balance_sheet: SectionData;
  income_statement: SectionData;
}

type TabType = 'pnl' | 'balance_sheet';
type StatementLevel = 2 | 3 | 'all';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtAmt(val: string | number | undefined, invert = false): string {
  if (!val || val === '0' || val === '0.0000') return '\u2014';
  let num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num) || num === 0) return '\u2014';
  if (invert) num = -num;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(Math.round(num)));
  return num < 0 ? `(${formatted})` : formatted;
}

function getYears(): number[] {
  const current = new Date().getFullYear();
  const years: number[] = [];
  for (let y = current + 1; y >= current - 5; y--) years.push(y);
  return years;
}

// ─── Level Filtering ────────────────────────────────────────────────────────

function filterBSRows(rows: AccountRow[], level: StatementLevel): AccountRow[] {
  if (level === 'all') return rows;
  if (level === 2) {
    // Only show subtotal rows (hide leaf accounts)
    return rows.filter((r) => r.is_subtotal);
  }
  // Level 3: hide deepest leaf accounts, keep everything else
  const maxIndent = Math.max(...rows.map((r) => r.indent_level ?? 0), 0);
  return rows.filter((r) => r.is_subtotal || (r.indent_level ?? 0) < maxIndent);
}

function filterPnLRows(rows: AccountRow[], level: StatementLevel, columns: Column[]): AccountRow[] {
  if (level === 'all') return rows;
  if (level === 2) {
    // Hide all rows — only section totals shown
    return [];
  }
  // Level 3: collapse children into parent group summaries
  const parentMap = new Map<string, AccountRow>();
  const standalone: AccountRow[] = [];
  for (const r of rows) {
    if (r.parent_name) {
      if (!parentMap.has(r.parent_name)) {
        parentMap.set(r.parent_name, {
          ...r,
          account_code: r.parent_code || '',
          account_name: r.parent_name,
          parent_name: null,
          parent_code: null,
          amounts: { ...r.amounts },
        });
      } else {
        const existing = parentMap.get(r.parent_name)!;
        for (const col of columns) {
          const cur = parseFloat(existing.amounts[col.key] || '0');
          const add = parseFloat(r.amounts[col.key] || '0');
          existing.amounts[col.key] = String(cur + add);
        }
      }
    } else {
      standalone.push(r);
    }
  }
  return [...standalone, ...parentMap.values()];
}

// ─── P&L Report Table ───────────────────────────────────────────────────────

function ReportTable({
  sections,
  columns,
  totals,
  variant,
  level,
  taxRows,
  discRows,
}: {
  sections: { key: string; title: string; rows: AccountRow[]; totalKey: string; totalLabel: string; invertSign?: boolean }[];
  columns: Column[];
  totals: Record<string, Record<string, string>>;
  variant: 'pnl' | 'bs';
  level: StatementLevel;
  taxRows?: AccountRow[];
  discRows?: AccountRow[];
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const colCount = columns.length + 1;

  const toggleGroup = (name: string) =>
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));

  const amtColWidth = `${Math.max(120, Math.floor(900 / Math.max(columns.length, 1)))}px`;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: `${320 + columns.length * 130}px`, borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '320px', minWidth: '320px' }} />
          {columns.map((c) => (
            <col key={c.key} style={{ width: amtColWidth, minWidth: '120px' }} />
          ))}
        </colgroup>

        {/* Header */}
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-4 font-semibold text-gray-700 sticky left-0 z-10 bg-white">
              Description
            </th>
            {columns.map((col) => (
              <th key={col.key} className="text-right py-2 px-3 font-semibold text-gray-700">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sections.map((section) => {
            const { key: sKey, title, rows, totalKey, totalLabel, invertSign } = section;
            const filteredRows = filterPnLRows(rows, level, columns);

            // Group by parent_name
            const parentGroups = new Map<string, AccountRow[]>();
            const standalone: AccountRow[] = [];
            filteredRows.forEach((r) => {
              if (r.parent_name) {
                if (!parentGroups.has(r.parent_name)) parentGroups.set(r.parent_name, []);
                parentGroups.get(r.parent_name)!.push(r);
              } else {
                standalone.push(r);
              }
            });

            return (
              <Fragment key={sKey}>
                {/* Section header */}
                <tr className="bg-gray-50">
                  <td
                    colSpan={colCount}
                    className="py-2 px-4 font-bold text-gray-800 text-sm uppercase tracking-wide"
                  >
                    {title}
                  </td>
                </tr>

                {/* Parent groups */}
                {[...parentGroups.entries()].map(([parentName, children]) => {
                  const isCollapsed = collapsed[`${sKey}-${parentName}`];
                  const subtotals: Record<string, number> = {};
                  columns.forEach((col) => {
                    subtotals[col.key] = children.reduce((sum, c) => {
                      const v = parseFloat(c.amounts[col.key] || '0');
                      return sum + (isNaN(v) ? 0 : v);
                    }, 0);
                  });

                  return (
                    <Fragment key={parentName}>
                      {/* Parent header row */}
                      <tr
                        className="cursor-pointer bg-blue-50/30 border-t border-gray-100 hover:bg-blue-50/50 transition-colors"
                        onClick={() => toggleGroup(`${sKey}-${parentName}`)}
                      >
                        <td className="py-1.5 px-4 text-xs font-semibold text-gray-700 sticky left-0 z-10 bg-blue-50/30">
                          <span className="inline-flex items-center gap-1">
                            {isCollapsed
                              ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                              : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                            <span className="font-mono text-gray-400 mr-1">{children[0]?.parent_code}</span>
                            {parentName}
                          </span>
                        </td>
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={clsx(
                              'px-3 py-1.5 text-xs text-right font-mono tabular-nums',
                              isCollapsed ? 'font-semibold text-gray-700' : 'text-gray-400',
                            )}
                          >
                            {isCollapsed ? fmtAmt(String(subtotals[col.key]), invertSign) : ''}
                          </td>
                        ))}
                      </tr>

                      {/* Child rows */}
                      {!isCollapsed &&
                        children.map((r) => (
                          <tr key={r.account_code} className="hover:bg-gray-50/50 transition-colors">
                            <td className="pl-10 pr-3 py-1 text-xs text-gray-600 sticky left-0 z-10 bg-white">
                              <span className="font-mono text-xs text-gray-400 mr-2">{r.account_code}</span>
                              {r.account_name}
                            </td>
                            {columns.map((col) => (
                              <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700">
                                {fmtAmt(r.amounts[col.key], invertSign)}
                              </td>
                            ))}
                          </tr>
                        ))}

                      {/* Parent subtotal */}
                      {!isCollapsed && children.length > 1 && (
                        <tr className="border-t border-gray-100 bg-gray-50/50">
                          <td className="py-1 pl-10 pr-2 text-gray-600 text-xs font-semibold italic sticky left-0 z-10 bg-gray-50/50">
                            Total {parentName}
                          </td>
                          {columns.map((col) => (
                            <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700 font-semibold">
                              {fmtAmt(String(subtotals[col.key]), invertSign)}
                            </td>
                          ))}
                        </tr>
                      )}
                    </Fragment>
                  );
                })}

                {/* Standalone rows (no parent) */}
                {standalone.map((r) => (
                  <tr key={r.account_code} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-1 text-xs text-gray-700 sticky left-0 z-10 bg-white">
                      <span className="font-mono text-xs text-gray-400 mr-2">{r.account_code}</span>
                      {r.account_name}
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700">
                        {fmtAmt(r.amounts[col.key], invertSign)}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Section total — #D6E4F0 style */}
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 px-4 font-semibold text-gray-700 text-xs sticky left-0 z-10" style={{ backgroundColor: '#D6E4F0' }}>
                    {totalLabel}
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-1.5 text-xs text-right font-mono tabular-nums font-semibold text-gray-800" style={{ backgroundColor: '#D6E4F0' }}>
                      {fmtAmt(totals[col.key]?.[totalKey], invertSign)}
                    </td>
                  ))}
                </tr>
                <tr><td colSpan={colCount} className="h-1" /></tr>
              </Fragment>
            );
          })}

          {/* P&L: PBT → Tax → PAT → Discontinued → Profit for Period */}
          {variant === 'pnl' && (
            <>
              {/* Profit Before Tax */}
              <tr>
                <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
                  Profit Before Tax
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                    {fmtAmt(totals[col.key]?.profit_before_tax)}
                  </td>
                ))}
              </tr>

              {/* Tax section */}
              {taxRows && taxRows.length > 0 && level !== 2 && (
                <>
                  <tr className="bg-gray-50">
                    <td colSpan={colCount} className="py-2 px-4 font-bold text-gray-800 text-sm uppercase tracking-wide">
                      Provision for Income Tax
                    </td>
                  </tr>
                  {taxRows.map((r) => (
                    <tr key={r.account_code} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-1 text-xs text-gray-700 sticky left-0 z-10 bg-white">
                        <span className="font-mono text-xs text-gray-400 mr-2">{r.account_code}</span>
                        {r.account_name}
                      </td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700">
                          {fmtAmt(r.amounts[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t border-gray-100">
                    <td className="py-1.5 px-4 font-semibold text-gray-700 text-xs sticky left-0 z-10" style={{ backgroundColor: '#D6E4F0' }}>
                      Total Provision for Income Tax
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-1.5 text-xs text-right font-mono tabular-nums font-semibold text-gray-800" style={{ backgroundColor: '#D6E4F0' }}>
                        {fmtAmt(totals[col.key]?.total_tax)}
                      </td>
                    ))}
                  </tr>
                </>
              )}

              {/* Profit After Tax */}
              <tr>
                <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
                  Profit After Tax
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                    {fmtAmt(totals[col.key]?.profit_after_tax)}
                  </td>
                ))}
              </tr>

              {/* Discontinued Operations (if any) */}
              {discRows && discRows.length > 0 && level !== 2 && (
                <>
                  {discRows.map((r) => (
                    <tr key={r.account_code} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-1 text-xs text-gray-700 sticky left-0 z-10 bg-white">
                        <span className="font-mono text-xs text-gray-400 mr-2">{r.account_code}</span>
                        {r.account_name}
                      </td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700">
                          {fmtAmt(r.amounts[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              )}

              {/* Profit for the Period (final) */}
              <tr>
                <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
                  Profit for the Period
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                    {fmtAmt(totals[col.key]?.net_income)}
                  </td>
                ))}
              </tr>
            </>
          )}

          {variant === 'bs' && (
            <>
              <tr>
                <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
                  Total Liabilities & Equity
                </td>
                {columns.map((col) => {
                  const t = totals[col.key];
                  if (!t) return <td key={col.key} className="px-3 py-2 text-xs text-right" style={{ backgroundColor: '#1F4E79' }}>{'\u2014'}</td>;
                  const sum = parseFloat(t.total_liabilities || '0') + parseFloat(t.total_equity || '0');
                  return (
                    <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                      {fmtAmt(String(sum))}
                    </td>
                  );
                })}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Hierarchical Report Table (Balance Sheet) ─────────────────────────────

function HierarchicalReportTable({
  sections,
  columns,
  totals,
  level,
}: {
  sections: { key: string; title: string; rows: AccountRow[]; totalKey: string; totalLabel: string; majorSection?: string; isMajorTotal?: boolean }[];
  columns: Column[];
  totals: Record<string, Record<string, string>>;
  level: StatementLevel;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const colCount = columns.length + 1;
  const amtColWidth = `${Math.max(120, Math.floor(900 / Math.max(columns.length, 1)))}px`;

  const toggleNode = (nodeId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const isRowHidden = (row: AccountRow, allRows: AccountRow[]): boolean => {
    let parentId = row.parent_node_id;
    while (parentId) {
      if (collapsed.has(parentId)) return true;
      const parentRow = allRows.find((r) => r.node_id === parentId);
      if (!parentRow) break;
      parentId = parentRow.parent_node_id;
    }
    return false;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: `${320 + columns.length * 130}px`, borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '320px', minWidth: '320px' }} />
          {columns.map((c) => (
            <col key={c.key} style={{ width: amtColWidth, minWidth: '120px' }} />
          ))}
        </colgroup>

        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-4 font-semibold text-gray-700 sticky left-0 z-10 bg-white">
              Description
            </th>
            {columns.map((col) => (
              <th key={col.key} className="text-right py-2 px-3 font-semibold text-gray-700">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sections.map((section, sIdx) => {
            const { key: sKey, title, rows, totalKey, totalLabel } = section;
            const majorSection = (section as any).majorSection as string | undefined;
            const isMajorTotal = (section as any).isMajorTotal as boolean | undefined;
            const filteredRows = filterBSRows(rows, level);

            // Track major section headers to only show once
            const prevMajor = sIdx > 0 ? (sections[sIdx - 1] as any).majorSection : undefined;
            const showMajorHeader = majorSection && majorSection !== prevMajor;

            // Major total row (e.g. "Total Assets") — rendered as a dark row
            if (isMajorTotal) {
              return (
                <Fragment key={sKey}>
                  <tr>
                    <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
                      {totalLabel}
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                        {fmtAmt(totals[col.key]?.[totalKey])}
                      </td>
                    ))}
                  </tr>
                  <tr><td colSpan={colCount} className="h-4" /></tr>
                </Fragment>
              );
            }

            return (
              <Fragment key={sKey}>
                {/* Major section header (ASSETS / EQUITY AND LIABILITIES) */}
                {showMajorHeader && (
                  <tr className="bg-gray-50">
                    <td
                      colSpan={colCount}
                      className="py-2.5 px-4 font-bold text-gray-800 text-sm uppercase tracking-wide border-t-2 border-gray-300"
                    >
                      {majorSection}
                    </td>
                  </tr>
                )}

                {/* Subsection header (e.g. Non-Current Assets, Current Assets, Equity) */}
                {title && (
                  <tr className="bg-gray-50/60">
                    <td
                      colSpan={colCount}
                      className="py-1.5 px-6 font-semibold text-gray-700 text-xs italic tracking-wide"
                    >
                      {title}
                    </td>
                  </tr>
                )}

                {/* Hierarchical rows */}
                {filteredRows.map((row, idx) => {
                  if (isRowHidden(row, filteredRows)) return null;

                  const indent = row.indent_level ?? 0;
                  const isSubtotal = row.is_subtotal ?? false;
                  const nodeId = row.node_id;
                  const isCollapsed = nodeId ? collapsed.has(nodeId) : false;
                  const paddingLeft = 16 + indent * 24;

                  if (isSubtotal) {
                    // Determine if this is a top-level subtotal (indent 0) or nested
                    if (indent === 0) {
                      // Top-level subtotal — use #D6E4F0 style (like "Total Non-Current Assets")
                      return (
                        <tr
                          key={`${sKey}-${row.account_code}-${idx}`}
                          className="border-t border-gray-100"
                          onClick={() => nodeId && toggleNode(nodeId)}
                          style={{ cursor: nodeId ? 'pointer' : undefined }}
                        >
                          <td
                            className="py-1.5 text-xs font-semibold text-gray-700 sticky left-0 z-10"
                            style={{ paddingLeft, backgroundColor: '#D6E4F0' }}
                          >
                            <span className="inline-flex items-center gap-1">
                              {nodeId && (
                                isCollapsed
                                  ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                              )}
                              {row.account_name}
                            </span>
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className="px-3 py-1.5 text-xs text-right font-mono tabular-nums font-semibold text-gray-800"
                              style={{ backgroundColor: '#D6E4F0' }}
                            >
                              {fmtAmt(row.amounts[col.key])}
                            </td>
                          ))}
                        </tr>
                      );
                    }

                    // Nested subtotal — subsection category header style (italic)
                    return (
                      <tr
                        key={`${sKey}-${row.account_code}-${idx}`}
                        className="bg-blue-50/30 border-t border-gray-100"
                        onClick={() => nodeId && toggleNode(nodeId)}
                        style={{ cursor: nodeId ? 'pointer' : undefined }}
                      >
                        <td
                          className="py-1.5 text-xs font-semibold text-gray-700 sticky left-0 z-10 bg-blue-50/30"
                          style={{ paddingLeft }}
                        >
                          <span className="inline-flex items-center gap-1">
                            {nodeId && (
                              isCollapsed
                                ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                            )}
                            <span className="font-mono text-[11px] text-gray-400 mr-1">{row.account_code}</span>
                            {row.account_name}
                          </span>
                        </td>
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className="px-3 py-1.5 text-xs text-right font-mono tabular-nums font-semibold text-gray-700"
                          >
                            {fmtAmt(row.amounts[col.key])}
                          </td>
                        ))}
                      </tr>
                    );
                  }

                  // Leaf row
                  return (
                    <tr
                      key={`${sKey}-${row.account_code}-${idx}`}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td
                        className="py-1 text-xs text-gray-600 sticky left-0 z-10 bg-white"
                        style={{ paddingLeft }}
                      >
                        <span className="font-mono text-xs text-gray-400 mr-2">{row.account_code}</span>
                        {row.account_name}
                      </td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-3 py-1 text-xs text-right font-mono tabular-nums text-gray-700">
                          {fmtAmt(row.amounts[col.key])}
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {/* Subsection total — #D6E4F0 style
                    Use the last subtotal row's amounts if available, otherwise fall back to totals object */}
                {filteredRows.length > 0 && (() => {
                  const lastRow = filteredRows[filteredRows.length - 1];
                  const useRowAmounts = lastRow?.is_subtotal && lastRow.amounts;
                  return (
                    <tr className="border-t border-gray-100">
                      <td className="py-1.5 px-6 font-semibold text-gray-700 text-xs sticky left-0 z-10" style={{ backgroundColor: '#D6E4F0' }}>
                        {totalLabel}
                      </td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-3 py-1.5 text-xs text-right font-mono tabular-nums font-semibold text-gray-800" style={{ backgroundColor: '#D6E4F0' }}>
                          {fmtAmt(useRowAmounts ? lastRow.amounts[col.key] : totals[col.key]?.[totalKey])}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                <tr><td colSpan={colCount} className="h-1" /></tr>
              </Fragment>
            );
          })}

          {/* Total Liabilities & Equity — #1F4E79 dark navy */}
          <tr>
            <td className="py-2 px-4 font-bold text-white text-xs sticky left-0 z-10" style={{ backgroundColor: '#1F4E79' }}>
              Total Liabilities & Equity
            </td>
            {columns.map((col) => {
              const t = totals[col.key];
              if (!t) return <td key={col.key} className="py-2 px-3 text-xs text-right text-white" style={{ backgroundColor: '#1F4E79' }}>{'\u2014'}</td>;
              const sum = parseFloat(t.total_liabilities || '0') + parseFloat(t.total_equity || '0');
              return (
                <td key={col.key} className="px-3 py-2 text-xs text-right font-mono tabular-nums font-bold text-white" style={{ backgroundColor: '#1F4E79' }}>
                  {fmtAmt(String(sum))}
                </td>
              );
            })}
          </tr>

        </tbody>
      </table>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function FinancialReports() {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [selectedScenario, setSelectedScenario] = useState('Actual');
  const [selectedView, setSelectedView] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [activeTab, setActiveTab] = useState<TabType>('pnl');
  const [statementLevel, setStatementLevel] = useState<StatementLevel>('all');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // Load entities
  useEffect(() => {
    entitiesApi.list({ page_size: 500 }).then((data: any) => {
      const list = Array.isArray(data) ? data : (data?.items ?? []);
      setEntities(list);
      if (list.length > 0 && !selectedEntity) {
        setSelectedEntity(String(list[0].id));
      }
    }).catch(() => {});
  }, []);

  const generateReport = useCallback(async () => {
    if (!selectedEntity) return;
    setLoading(true);
    setError(null);
    try {
      const data = await reportsApi.financialReports({
        entity_id: Number(selectedEntity),
        year: selectedYear,
        scenario: selectedScenario,
        view: selectedView,
      });
      setReportData(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to generate report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, selectedYear, selectedScenario, selectedView]);

  const handlePrint = () => window.print();

  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  const handleExportXLSX = async () => {
    if (!reportData || !selectedEntity) return;
    setExporting(true);
    try {
      const blob = await reportsApi.financialReportsExport({
        entity_id: Number(selectedEntity),
        year: selectedYear,
        scenario: selectedScenario,
        view: selectedView,
        report_type: activeTab === 'pnl' ? 'pnl' : 'balance_sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_${reportData.entity_code}_${reportData.year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const blob = await reportsApi.financialReportsExportAll({
        year: selectedYear,
        scenario: selectedScenario,
        view: selectedView,
        report_type: 'both',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all_financial_reports_${selectedYear}_${selectedScenario}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export all failed:', err);
    } finally {
      setExportingAll(false);
    }
  };

  const tabs: { key: TabType; label: string; icon: React.ElementType }[] = [
    { key: 'pnl', label: 'P&L Statement', icon: TrendingUp },
    { key: 'balance_sheet', label: 'Balance Sheet', icon: BarChart3 },
  ];

  const entityObj = entities.find((e) => String(e.id) === selectedEntity);
  const currencyLabel = reportData?.currency || entityObj?.functional_currency || 'AED';

  // Build section configs for the unified table
  const pnlSections = reportData
    ? [
        { key: 'revenue', title: 'Revenue', rows: reportData.income_statement.sections.revenue || [], totalKey: 'total_revenue', totalLabel: 'Total Revenue' },
        { key: 'expenses', title: 'Operating Expenses', rows: reportData.income_statement.sections.expenses || [], totalKey: 'total_expenses', totalLabel: 'Total Operating Expenses' },
      ]
    : [];

  // Tax & discontinued ops sections (rendered separately after PBT)
  const pnlTaxRows = reportData?.income_statement?.sections?.tax || [];
  const pnlDiscRows = reportData?.income_statement?.sections?.discontinued || [];

  // Build BS sections matching Trial Balances order:
  // ASSETS: Non-current assets → Current assets → Total Assets
  // EQUITY AND LIABILITIES: Equity → Non-current liabilities → Current liabilities → Total E&L
  const bsSections = reportData
    ? (() => {
        const assetRows = reportData.balance_sheet.sections.assets || [];
        const equityRows = reportData.balance_sheet.sections.equity || [];
        const liabRows = reportData.balance_sheet.sections.liabilities || [];

        // Split assets into non-current and current using FCCS hierarchy markers
        const ncaEndIdx = assetRows.findIndex(
          (r: AccountRow) => r.is_subtotal && r.indent_level === 1 &&
            (r.account_name?.toLowerCase().includes('long term') ||
             r.account_name?.toLowerCase().includes('non-current') ||
             r.account_code === 'FCCS_Long Term Assets')
        );
        const caEndIdx = assetRows.findIndex(
          (r: AccountRow) => r.is_subtotal && r.indent_level === 1 &&
            (r.account_name?.toLowerCase().includes('current assets') ||
             r.account_code === 'FCCS_Current Assets')
        );

        let nonCurrentAssets: AccountRow[] = [];
        let currentAssets: AccountRow[] = [];

        if (ncaEndIdx >= 0 && caEndIdx >= 0 && ncaEndIdx < caEndIdx) {
          nonCurrentAssets = assetRows.slice(0, ncaEndIdx + 1);
          currentAssets = assetRows.slice(ncaEndIdx + 1, caEndIdx + 1);
        } else {
          // Fallback: show all as-is under assets
          nonCurrentAssets = assetRows;
        }

        // Split liabilities into non-current and current
        const nclEndIdx = liabRows.findIndex(
          (r: AccountRow) => r.is_subtotal && r.indent_level === 1 &&
            (r.account_name?.toLowerCase().includes('long term') ||
             r.account_name?.toLowerCase().includes('non-current') ||
             r.account_code === 'FCCS_Long Term Liabilities')
        );
        const clEndIdx = liabRows.findIndex(
          (r: AccountRow) => r.is_subtotal && r.indent_level === 1 &&
            (r.account_name?.toLowerCase().includes('current liabilities') ||
             r.account_code === 'FCCS_Current Liabilities')
        );

        let nonCurrentLiab: AccountRow[] = [];
        let currentLiab: AccountRow[] = [];

        if (nclEndIdx >= 0 && clEndIdx >= 0 && nclEndIdx < clEndIdx) {
          nonCurrentLiab = liabRows.slice(0, nclEndIdx + 1);
          currentLiab = liabRows.slice(nclEndIdx + 1, clEndIdx + 1);
        } else if (nclEndIdx >= 0) {
          nonCurrentLiab = liabRows.slice(0, nclEndIdx + 1);
          currentLiab = liabRows.slice(nclEndIdx + 1);
        } else {
          currentLiab = liabRows;
        }

        return [
          // ASSETS major section
          { key: 'non_current_assets', title: 'Non-Current Assets', rows: nonCurrentAssets, totalKey: 'total_non_current_assets', totalLabel: 'Total Non-Current Assets', majorSection: 'ASSETS' },
          { key: 'current_assets', title: 'Current Assets', rows: currentAssets, totalKey: 'total_current_assets', totalLabel: 'Total Current Assets', majorSection: 'ASSETS' },
          { key: 'assets_total', title: '', rows: [], totalKey: 'total_assets', totalLabel: 'Total Assets', majorSection: 'ASSETS', isMajorTotal: true },
          // EQUITY AND LIABILITIES major section
          { key: 'equity', title: 'Equity', rows: equityRows, totalKey: 'total_equity', totalLabel: 'Total Equity', majorSection: 'EQUITY AND LIABILITIES' },
          { key: 'non_current_liabilities', title: 'Non-Current Liabilities', rows: nonCurrentLiab, totalKey: 'total_non_current_liabilities', totalLabel: 'Total Non-Current Liabilities', majorSection: 'EQUITY AND LIABILITIES' },
          { key: 'current_liabilities', title: 'Current Liabilities', rows: currentLiab, totalKey: 'total_current_liabilities', totalLabel: 'Total Current Liabilities', majorSection: 'EQUITY AND LIABILITIES' },
        ];
      })()
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileBarChart className="w-6 h-6 text-primary-600" />
            Financial Reports
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {reportData
              ? `${reportData.entity_name} \u00b7 FY ${reportData.year} \u00b7 ${currencyLabel}`
              : 'Balance Sheet, P&L, and Variance Analysis'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportAll} disabled={exportingAll}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition shadow-sm">
            {exportingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exportingAll ? 'Exporting All...' : 'Export All Entities'}
          </button>
          {reportData && (
            <>
              <button onClick={handleExportXLSX} disabled={exporting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export Excel
              </button>
              <button onClick={handlePrint}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                <Printer className="w-4 h-4" /> Print
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b px-6 py-4 print:hidden">
        <div className="flex flex-wrap items-end gap-4">
          {/* Entity */}
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Entity</label>
            <SearchableSelect
              options={entities.map((e) => ({ value: String(e.id), label: e.name, secondary: e.code }))}
              value={selectedEntity}
              onChange={setSelectedEntity}
              placeholder="Select entity..."
            />
          </div>

          {/* Scenario */}
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Scenario</label>
            <select className="w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={selectedScenario} onChange={(e) => setSelectedScenario(e.target.value)}>
              <option value="Actual">Actual</option>
              <option value="Budget">Budget</option>
              <option value="Forecast">Forecast</option>
            </select>
          </div>

          {/* Year */}
          <div className="w-28">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Year</label>
            <select className="w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {getYears().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* View */}
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">View</label>
            <select className="w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={selectedView} onChange={(e) => setSelectedView(e.target.value as any)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateReport}
            disabled={!selectedEntity || loading}
            className="px-6 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Generate Report
          </button>
        </div>
      </div>

      {/* Tabs & Content */}
      <div className="px-6 py-4">
        {reportData ? (
          <>
            {/* Tabs + Level Selector */}
            <div className="flex items-center justify-between border-b border-gray-200 mb-4 print:hidden">
              <div className="flex gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={clsx(
                        'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                        activeTab === tab.key
                          ? 'border-teal-600 text-teal-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-3">
                {/* Level selector */}
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                  {([2, 3, 'all'] as StatementLevel[]).map((lvl) => (
                    <button
                      key={String(lvl)}
                      onClick={() => setStatementLevel(lvl)}
                      className={clsx(
                        'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                        statementLevel === lvl
                          ? 'bg-white text-primary-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700',
                      )}
                    >
                      {lvl === 'all' ? 'All Levels' : `Level ${lvl}`}
                    </button>
                  ))}
                </div>

                <div className="text-xs text-gray-400">
                  Balance Type: <span className="font-semibold text-gray-600 uppercase">{reportData.balance_type}</span>
                </div>
              </div>
            </div>

            {/* Report Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" ref={tableRef}>
              {activeTab === 'pnl' && (
                <ReportTable
                  sections={pnlSections}
                  columns={reportData.columns}
                  totals={reportData.income_statement.totals}
                  variant="pnl"
                  level={statementLevel}
                  taxRows={pnlTaxRows}
                  discRows={pnlDiscRows}
                />
              )}

              {activeTab === 'balance_sheet' && (
                <HierarchicalReportTable
                  sections={bsSections}
                  columns={reportData.columns}
                  totals={reportData.balance_sheet.totals}
                  level={statementLevel}
                />
              )}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            {error ? (
              <>
                <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">Error</h3>
                <p className="text-sm text-red-500 max-w-md">{error}</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">No Report Generated</h3>
                <p className="text-sm text-gray-500 max-w-md">
                  Select an entity, scenario, and year, then click Generate Report to view financial statements.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
